import React, { useState } from 'react';
import { styled, XStack as BaseXStack, YStack } from 'tamagui';

import { Checkbox, CheckboxWithLabel } from '../../components/inputs/Checkbox';
import { CircularSelectionIndicator } from '../../components/inputs/CircularSelectionIndicator';
import {
    RadioInput,
    RadioInputWithLabel,
} from '../../components/inputs/RadioInput';
import { BodyMediumEmphasized } from '../../components/text/Typography';

const XStack = styled(BaseXStack, { paddingHorizontal: 20, gap: 20 });

export const CheckboxesAndRadios = () => {
    const [checkedStates, setCheckedStates] = useState([
        0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 1, 0, 1, 0, 0, 0,
    ]);
    const toggleCheckboxState = (index: number) => {
        setCheckedStates((prev) => {
            const newCheckedStates = [...prev];
            newCheckedStates[index] = newCheckedStates[index] === 0 ? 1 : 0;
            return newCheckedStates;
        });
    };

    const [selectedStates, setSelectedStates] = useState([
        0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1,
    ]);
    const toggleSelectedState = (index: number) => {
        setSelectedStates((prev) => {
            const newSelectedStates = [...prev];
            newSelectedStates[index] = newSelectedStates[index] === 0 ? 1 : 0;
            return newSelectedStates;
        });
    };

    return (
        <YStack gap={48}>
            <YStack gap={16}>
                <BodyMediumEmphasized>
                    Checkbox & CheckboxWithLabel
                </BodyMediumEmphasized>
                <YStack
                    gap={12}
                    $md={{
                        flexDirection: 'row',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                    }}
                >
                    <XStack>
                        <Checkbox
                            id="cb0"
                            checked={checkedStates[0] === 1}
                            onPress={() => {
                                toggleCheckboxState(0);
                            }}
                        />
                        <CheckboxWithLabel
                            label="default size, neutral tone"
                            id="cb1"
                            checked={checkedStates[1] === 1}
                            onPress={() => {
                                toggleCheckboxState(1);
                            }}
                        />
                    </XStack>
                    <XStack>
                        <Checkbox
                            id="cb2"
                            size="large"
                            checked={checkedStates[2] === 1}
                            onPress={() => {
                                toggleCheckboxState(2);
                            }}
                        />
                        <CheckboxWithLabel
                            label="large size, default tone"
                            id="cb3"
                            size="large"
                            checked={checkedStates[3] === 1}
                            onPress={() => {
                                toggleCheckboxState(3);
                            }}
                        />
                    </XStack>
                    <XStack>
                        <Checkbox
                            id="cb4"
                            tone="positive"
                            checked={checkedStates[4] === 1}
                            onPress={() => {
                                toggleCheckboxState(4);
                            }}
                        />
                        <CheckboxWithLabel
                            label="default size, positive tone"
                            id="cb5"
                            tone="positive"
                            checked={checkedStates[5] === 1}
                            onPress={() => {
                                toggleCheckboxState(5);
                            }}
                        />
                    </XStack>
                    <XStack>
                        <Checkbox
                            id="cb6"
                            size="large"
                            tone="positive"
                            checked={checkedStates[6] === 1}
                            onPress={() => {
                                toggleCheckboxState(6);
                            }}
                        />
                        <CheckboxWithLabel
                            label="large size, positive tone"
                            id="cb7"
                            size="large"
                            tone="positive"
                            checked={checkedStates[7] === 1}
                            onPress={() => {
                                toggleCheckboxState(7);
                            }}
                        />
                    </XStack>
                    <XStack>
                        <Checkbox
                            id="cb8"
                            tone="positive"
                            disabled
                            checked={checkedStates[8] === 1}
                            onPress={() => {
                                toggleCheckboxState(8);
                            }}
                        />
                        <CheckboxWithLabel
                            label="disabled, default size, any tone"
                            id="cb9"
                            tone="positive"
                            disabled
                            checked={checkedStates[9] === 1}
                            onPress={() => {
                                toggleCheckboxState(9);
                            }}
                        />
                    </XStack>
                    <XStack>
                        <Checkbox
                            id="cb10"
                            size="large"
                            disabled
                            checked={checkedStates[10] === 1}
                            onPress={() => {
                                toggleCheckboxState(10);
                            }}
                        />
                        <CheckboxWithLabel
                            label="disabled, large size, any tone"
                            id="cb11"
                            size="large"
                            disabled
                            checked={checkedStates[11] === 1}
                            onPress={() => {
                                toggleCheckboxState(11);
                            }}
                        />
                    </XStack>
                </YStack>
            </YStack>
            <YStack gap={16}>
                <BodyMediumEmphasized>
                    RadioInput & RadioInputWithLabel
                </BodyMediumEmphasized>
                <YStack
                    gap={12}
                    $md={{
                        flexDirection: 'row',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                    }}
                >
                    <XStack alignItems="center">
                        <RadioInput
                            isSelected={selectedStates[0] === 1}
                            onPress={() => {
                                toggleSelectedState(0);
                            }}
                        />
                        <RadioInputWithLabel
                            label="normal"
                            isSelected={selectedStates[1] === 1}
                            onPress={() => {
                                toggleSelectedState(1);
                            }}
                        />
                    </XStack>
                    <XStack alignItems="center">
                        <RadioInput
                            tone="positive"
                            isSelected={selectedStates[2] === 1}
                            onPress={() => {
                                toggleSelectedState(2);
                            }}
                        />
                        <RadioInputWithLabel
                            tone="positive"
                            label="positive tone"
                            isSelected={selectedStates[3] === 1}
                            onPress={() => {
                                toggleSelectedState(3);
                            }}
                        />
                    </XStack>
                    <XStack alignItems="center">
                        <RadioInput
                            disabled
                            isSelected={selectedStates[4] === 1}
                            onPress={() => {
                                toggleSelectedState(4);
                            }}
                        />
                        <RadioInputWithLabel
                            disabled
                            label="disabled"
                            isSelected={selectedStates[5] === 1}
                            onPress={() => {
                                toggleSelectedState(5);
                            }}
                        />
                    </XStack>
                </YStack>
            </YStack>
            <YStack gap={16}>
                <BodyMediumEmphasized>
                    CircularSelectionIndicator
                </BodyMediumEmphasized>
                <XStack alignItems="center" gap={24}>
                    <CircularSelectionIndicator
                        selected={checkedStates[0] === 1}
                        onPress={() => {
                            toggleCheckboxState(0);
                        }}
                    />
                    <CircularSelectionIndicator
                        selected={checkedStates[1] === 1}
                        onPress={() => {
                            toggleCheckboxState(1);
                        }}
                    />
                    <CircularSelectionIndicator selected disabled />
                </XStack>
            </YStack>
        </YStack>
    );
};
